import React, { useCallback } from 'react';
import styled from 'astroturf/react';

import { type FormatResult } from 'js/citation_generator/format';
import { sanitizeCitationHtml } from 'js/citation_generator/sanitize';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { Heading } from 'js/design_system/Heading';
import { useToast } from 'js/design_system/toasts/hooks';

import { copyCitation } from './copy';

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  width: 100%;
  padding-top: var(--spacing-xs-16, 16px);
  border-top: 1px solid var(--stroke-light, #dddde2);
`;

const OutputRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);
  width: 100%;
`;

const OutputHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-xs-16, 16px);
`;

const CitationHtml = styled(Body)`
  margin: 0;
`;

type CitationOutputProps = {
  result: FormatResult | null;
};

function OutputBlock({
  label,
  html,
}: {
  label: string;
  html: string;
}): React.ReactElement {
  const { infoToast, errorToast } = useToast();

  const onCopy = useCallback((): void => {
    copyCitation(html)
      .then(() => {
        infoToast('Copied');
      })
      .catch(() => {
        errorToast('Could not copy');
      });
  }, [html, infoToast, errorToast]);

  return (
    <OutputRow>
      <OutputHeader>
        <Heading as="h3" size="xs">
          {label}
        </Heading>
        <Button
          type="button"
          size="sm"
          secondary
          icon="content_copy"
          onClick={onCopy}
        >
          Copy
        </Button>
      </OutputHeader>
      <CitationHtml
        as="div"
        // Citation HTML is allowlisted in sanitizeCitationHtml.
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: sanitizeCitationHtml(html) }}
      />
    </OutputRow>
  );
}

export function CitationOutput({
  result,
}: CitationOutputProps): React.ReactElement {
  return (
    <Section>
      <Heading as="h2" size="sm">
        Citation
      </Heading>
      {result ? (
        <>
          <OutputBlock label="Reference list" html={result.referenceEntry} />
          <OutputBlock label="In-text citation" html={result.inTextCitation} />
          {result.inTextCitationNarrative ? (
            <OutputBlock
              label="Narrative citation"
              html={result.inTextCitationNarrative}
            />
          ) : null}
        </>
      ) : (
        <Body>Enter a title, author, or URL to see a formatted citation.</Body>
      )}
    </Section>
  );
}
